import { useAuthStore } from '@/stores/auth'
import { useLocalesStore } from '@/stores/locales'
import { useMaintenanceStore } from '@/stores/maintenance'
import { useMenuStore } from '@/stores/menu'
import { useServerSelectionStore } from '@/stores/serverSelection'
import { useUiStore } from '@/stores/ui'
import { useUserStore } from '@/stores/user'
import type { LoginPayload, LoginResult, SocialProvider, User } from '@/types/user'
import { applyAccountPreference } from '@/utils/dateFormatPreference'
import { applyAccountTheme } from '@/utils/themePreference'

/**
 * Cross-store flows. Each store stays focused on its own slice; the sequencing
 * that spans several of them (log in, log out, switch group) lives here.
 *
 * These are plain functions rather than a composable: router guards need them
 * too, and they must work outside a component's setup context.
 */

/**
 * Makes the app state consistent with whatever is in storage. Safe to call on
 * every navigation: it only does work when something is actually missing.
 */
export async function bootstrapSession(): Promise<void> {
  const auth = useAuthStore()
  const user = useUserStore()
  const menu = useMenuStore()
  const maintenance = useMaintenanceStore()
  const locales = useLocalesStore()

  // Lingue e traduzioni prima di tutto il resto, e in parallelo allo stato della
  // manutenzione: nessuna schermata ha senso senza le proprie stringhe, e la
  // pagina di avviso manutenzione è la prima che potrebbe servirle. Lo store
  // ingoia i suoi errori e ricade sull'inglese del bundle.
  //
  // Lo stato manutenzione si legge una volta per sessione: la guard lo vuole
  // prima di decidere qualsiasi cosa, e un fallimento viene ingoiato dallo store
  // (un endpoint di stato irraggiungibile non deve chiudere fuori nessuno — il
  // 503 dell'API resta la fonte di verità).
  await Promise.all([
    locales.ensureReady(),
    maintenance.loaded ? Promise.resolve() : maintenance.fetch(),
  ])

  if (auth.isAuthenticated && user.user === null) {
    try {
      const fetched = await user.fetchUser()
      applyAccountPreference(fetched.date_format)
      applyAccountTheme(fetched.theme)
      applyAccountTheme(fetched.theme)
      await alignGroupAndServer()
    } catch {
      // The HTTP layer already handled a dead session (refresh + logout) and
      // notified the user; carry on as a guest.
    }
  }

  if (!menu.loaded) {
    try {
      await menu.fetch()
    } catch {
      // Navigation must survive a menu outage.
    }
  }
}

/** Password login, then load everything that depends on the identity. */
export async function signIn(payload: LoginPayload): Promise<LoginResult> {
  const auth = useAuthStore()
  const result = await auth.login(payload)
  await loadIdentity()
  return result
}

/** Social login (the provider's token has already been obtained client-side). */
export async function signInWithProvider(
  provider: SocialProvider,
  token: string,
): Promise<LoginResult> {
  const auth = useAuthStore()
  const result = await auth.socialLogin(provider, token)
  await loadIdentity()
  return result
}

/** Second half of the Discord SSO redirect (see `stores/auth.ts`), then load everything else. */
export async function signInWithDiscordTicket(ticket: string): Promise<LoginResult> {
  const auth = useAuthStore()
  const result = await auth.exchangeDiscordLoginTicket(ticket)
  await loadIdentity()
  return result
}

/** Fetches the profile and the personalised menu for the current token. */
export async function loadIdentity(): Promise<User> {
  const user = useUserStore()
  const menu = useMenuStore()
  const fetched = await user.fetchUser()
  applyAccountPreference(fetched.date_format)
  applyAccountTheme(fetched.theme)
  await alignGroupAndServer().catch(() => undefined)
  // The menu differs per user/role/group, so the guest tree must be replaced.
  await menu.reload().catch(() => undefined)
  return fetched
}

/**
 * Keeps the active group and the active server consistent with each other.
 *
 * A group is bound to exactly one server (`Group.server_id`), so it must
 * dominate: whichever group ends up selected drives which server is shown,
 * even if that server isn't a favourite yet (it gets added automatically).
 * When the account has exactly one group and none is selected yet, that group
 * becomes the active one — there is nothing else it could sensibly be.
 */
async function alignGroupAndServer(): Promise<void> {
  const user = useUserStore()

  if (!user.selectedGroup && user.groups.length === 1) {
    const [onlyGroup] = user.groups
    if (onlyGroup) await user.selectGroup(onlyGroup.id)
  }

  const group = user.selectedGroup
  if (!group) return

  const isFavorite = user.favoriteServers.some((server) => server.id === group.server_id)
  if (!isFavorite) await user.addFavoriteServer(group.server_id)

  useServerSelectionStore().select(group.server_id)
}

/**
 * Cambia la lingua dell'interfaccia.
 *
 * Attraversa tre store (traduzioni, velo di attesa, profilo), quindi vive qui e
 * non dentro uno di essi. L'ordine conta: prima si caricano i messaggi, poi si
 * sposta il locale. Se il caricamento fallisce **non si cambia niente** — meglio
 * restare nella lingua di prima che ritrovarsi un'interfaccia mezza inglese; il
 * toast lo alza già il layer HTTP.
 *
 * Salvare la scelta sull'account è l'ultimo passo e non è bloccante: la lingua è
 * già cambiata sotto gli occhi dell'utente, e un errore lì non deve annullarla.
 */
export async function changeLanguage(iso: string): Promise<void> {
  const locales = useLocalesStore()
  const auth = useAuthStore()
  const user = useUserStore()
  const ui = useUiStore()

  await ui.withLoading(() => locales.use(iso))

  if (!auth.isAuthenticated) return

  try {
    await user.updateProfile({ language: iso })
  } catch {
    // Il toast arriva dal layer HTTP; la lingua resta quella scelta, e la scelta
    // è comunque salvata in localStorage per la prossima visita.
  }
}

/** Clears the session and falls back to the guest menu. */
export async function signOut(): Promise<void> {
  const auth = useAuthStore()
  const user = useUserStore()
  const menu = useMenuStore()

  await auth.logout()
  user.clear()
  await menu.reload().catch(() => undefined)
}

/**
 * Switches the active group and reloads the menu, because a different role in
 * the new group can add or remove entries.
 *
 * @returns whether `currentPath` is still reachable afterwards, so the caller
 *          can bounce the user to the dashboard when it is not.
 */
export async function switchGroup(
  groupId: number | null,
  currentPath?: string,
): Promise<{ stillAllowed: boolean }> {
  const user = useUserStore()
  const menu = useMenuStore()

  if (groupId === null) {
    await user.unselectGroup()
  } else {
    await user.selectGroup(groupId)
  }
  // Same as `loadIdentity()`: the favourite-server side effect is unrelated to
  // the menu, and must never be able to skip its reload — the role has already
  // changed at this point (`selectGroup`/`unselectGroup` awaited `fetchUser`),
  // so the sidebar would otherwise stay on the old tree until a hard refresh.
  await alignGroupAndServer().catch(() => undefined)
  await menu.reload()

  if (currentPath === undefined) return { stillAllowed: true }
  return { stillAllowed: menu.isAllowed(currentPath) }
}

/**
 * Leaves a group and reloads the menu, since leaving the active group changes
 * role just like switching away from it. `alignGroupAndServer` promotes the
 * one group left, if any, to selected — same as after a `switchGroup`.
 *
 * @returns whether `currentPath` is still reachable afterwards, so the caller
 *          can bounce the user to the dashboard when it is not.
 */
export async function leaveGroup(
  groupId: number,
  currentPath?: string,
): Promise<{ stillAllowed: boolean }> {
  const user = useUserStore()
  const menu = useMenuStore()

  await user.leaveGroup(groupId)
  await alignGroupAndServer().catch(() => undefined)
  await menu.reload()

  if (currentPath === undefined) return { stillAllowed: true }
  return { stillAllowed: menu.isAllowed(currentPath) }
}
